import { useCallback } from 'react'
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useTracks,
  useConnectionState,
  useRoomInfo,
  useLocalParticipant,
} from '@livekit/components-react'
import '@livekit/components-styles'
import { Track, VideoPresets } from 'livekit-client'
import { PlaygroundTile } from './PlaygroundTile'
import { PlaygroundTabbedTile } from './PlaygroundTabbedTile'
import { PlaygroundHeader } from './PlaygroundHeader'
import { ConversationTile } from '../chat/ConversationTile'
import { AudioInputTile } from '../config/AudioInputTile'
import { DisconnectedState } from './DisconnectedState'
import { SettingsPanel } from './SettingsPanel'
import { VideoTrackDisplay } from './VideoTrackDisplay'
import { VideoToggle } from './VideoToggle'
import { AudioToggle } from './AudioToggle'
import { useConfig } from '../../hooks/useConfig'
import { useConnection } from '../../hooks/useConnection'
import { useAutoConnect } from '../../hooks/useAutoConnect'
import { showToast } from '../toast/PlaygroundToast'
import { PLAYGROUND_CONSTANTS } from '../../constants/playground'

export function Playground() {
  const { config } = useConfig()
  const { shouldConnect, wsUrl, token, connect, disconnect } = useConnection()

  // Auto-connect on mount
  useAutoConnect({ shouldConnect, connect })

  // Handle connect/disconnect
  const handleConnectClick = useCallback(async () => {
    console.log(
      '[Playground] handleConnectClick called, shouldConnect:',
      shouldConnect,
    )
    if (shouldConnect) {
      console.log('[Playground] Disconnecting...')
      await disconnect()
    } else {
      console.log('[Playground] Connecting...')
      try {
        await connect('env')
        console.log('[Playground] Connect successful')
      } catch (error) {
        console.error('[Playground] Connect failed:', error)
        showToast.error('Failed to connect to LiveKit')
      }
    }
  }, [shouldConnect, connect, disconnect])

  // Show disconnected state
  if (!shouldConnect || !wsUrl || !token) {
    return (
      <DisconnectedState config={config} onConnectClick={handleConnectClick} />
    )
  }

  return (
    <LiveKitRoom
      serverUrl={wsUrl}
      token={token}
      connect={shouldConnect}
      audio={config.settings.outputs.audio}
      video={false}
      onDisconnected={handleConnectClick}
      options={{
        adaptiveStream: config.settings.adaptiveStream,
        dynacast: config.settings.dynacast,
        publishDefaults: {
          simulcast: config.settings.simulcast,
          videoCodec: config.settings.videoCodec as
            | 'vp8'
            | 'h264'
            | 'vp9'
            | 'av1'
            | 'h265'
            | undefined,
        },
        videoCaptureDefaults: {
          resolution: VideoPresets.h720.resolution,
        },
        audioCaptureDefaults: {
          echoCancellation: true,
          noiseSuppression: true,
        },
      }}
      className="h-screen bg-background"
    >
      <PlaygroundInner onLeave={handleConnectClick} />
      <RoomAudioRenderer />
    </LiveKitRoom>
  )
}

interface PlaygroundInnerProps {
  onLeave: () => void
}

function PlaygroundInner({ onLeave }: PlaygroundInnerProps) {
  const { config } = useConfig()
  const roomInfo = useRoomInfo()
  const { localParticipant } = useLocalParticipant()
  const connectionState = useConnectionState()

  const videoTracks = useTracks([Track.Source.Camera], {
    onlySubscribed: false,
  })

  const videoContent = <VideoTrackDisplay tracks={videoTracks} />
  const audioContent = <AudioInputTile />
  const conversationContent = <ConversationTile />

  const settingsContent = (
    <SettingsPanel
      description={config.description}
      roomInfo={roomInfo}
      localParticipant={localParticipant}
      connectionState={connectionState}
    />
  )

  return (
    <div className="flex flex-col h-full w-full px-4">
      <PlaygroundHeader
        title={config.title}
        height={PLAYGROUND_CONSTANTS.HEADER_HEIGHT}
        accentColor={config.settings.theme_color}
        connectionState={connectionState}
        onConnectClicked={onLeave}
      />

      <div
        className="flex gap-4 py-4 grow w-full"
        style={{
          height: `calc(100% - ${PLAYGROUND_CONSTANTS.HEADER_HEIGHT}px)`,
        }}
      >
        {/* Desktop: Three-column layout */}
        {(config.settings.outputs.video || config.settings.outputs.audio) && (
          <div className="flex-col grow basis-1/2 gap-4 h-full hidden lg:flex">
            {config.settings.outputs.video && (
              <PlaygroundTile
                className="w-full h-full grow flex flex-col"
                childrenClassName="justify-center relative"
                padding={false}
              >
                {videoContent}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                  <VideoToggle />
                </div>
              </PlaygroundTile>
            )}
            {config.settings.outputs.audio && (
              <PlaygroundTile
                className="w-full h-full grow flex flex-col"
                childrenClassName="justify-center relative"
              >
                {audioContent}
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                  <AudioToggle />
                </div>
              </PlaygroundTile>
            )}
          </div>
        )}

        {/* Conversation column (chat + transcription) */}
        {config.settings.chat && (
          <PlaygroundTile
            className="h-full grow basis-1/4 hidden lg:flex flex-col"
            padding={false}
          >
            {conversationContent}
          </PlaygroundTile>
        )}

        <PlaygroundTile
          padding={false}
          className={`h-full w-full basis-1/4 items-start overflow-y-auto hidden max-w-[${PLAYGROUND_CONSTANTS.SETTINGS_PANEL_MAX_WIDTH}px] lg:flex flex-col bg-card`}
          childrenClassName="h-full grow items-start"
        >
          {settingsContent}
        </PlaygroundTile>

        {/* Mobile: Tabbed layout */}
        <PlaygroundTabbedTile
          className="flex flex-col grow h-full lg:hidden"
          tabs={[
            { title: 'Video', content: videoContent },
            { title: 'Audio', content: audioContent },
            ...(config.settings.chat
              ? [{ title: 'Conversation', content: conversationContent }]
              : []),
            { title: 'Settings', content: settingsContent },
          ]}
        />
      </div>
    </div>
  )
}
